import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCustomToast } from '@nexelem/snackbar';
import { useTableTranslation } from '../translations';
import type { TableState } from '../utils/TableState';
import { useTableData } from '../utils/TableDataProvider';
import { exportTableToCsv, formatExportFileName } from './exportTableToCsv';

type UseExportToCsvProps = {
  fileName: string;
};

export const useExportToCsv = ({ fileName }: UseExportToCsvProps) => {
  const { t } = useTableTranslation();
  const { columns, dataSource } = useTableData();
  const { getValues } = useFormContext<TableState>();
  const displayToast = useCustomToast();
  const [isExporting, setIsExporting] = useState(false);

  const exportToCsv = useCallback(async () => {
    setIsExporting(true);
    displayToast({
      message: t('csvExportStarted'),
      toastType: 'info',
    });

    try {
      const { filters, ordering, visibility, columnOrder, columnPinning } = getValues();
      await exportTableToCsv({
        dataSource,
        columns,
        filters,
        ordering,
        visibility,
        columnOrder,
        columnPinning,
        fileName: formatExportFileName(fileName),
      });
      displayToast({
        message: t('csvExportSuccess'),
        toastType: 'success',
      });
    } catch {
      displayToast({
        message: t('csvExportError'),
        toastType: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  }, [columns, dataSource, displayToast, getValues, t, fileName]);

  return { exportToCsv, isExporting };
};
